/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { featureLifecyleHooks, printAnalyticsEvents, printTriggerType } from "constants/enums";
import { CONTENT_DOCUMENT_NOT_SET } from "constants/constants";
import { isEdgeHtml } from "dc-preview-utils/src/Utils/UserAgentUtils";

export default class PrintUsingBrowserPrint {
  constructor() {
    this.pdfBlobUrl = null;
    this.printFrame = null;
    this.onLoad = this.onLoad.bind(this);
    window.addEventListener("message", evt => {
      if (evt.origin === window.location.origin && evt.source === window.parent) {
        let data = evt.data;
        if (data.message === "doPrint" && data.buffer) {
          this.print(data.buffer);
        }
      }
    });
  }

  triggerPostMessageForAfterPrintCallback(statusFlag) {
    window.parent.postMessage(
      { message: featureLifecyleHooks.PRINT_COMPLETED, success: statusFlag },
      window.location.origin
    );
  }

  triggerLogAnalyticsCallback(analyticsEventType) {
    window.parent.postMessage({ message: analyticsEventType }, window.location.origin);
  }

  triggerPostMessageForFallbackPrint(fallbackPrintEvent) {
    window.parent.postMessage({ message: fallbackPrintEvent }, window.location.origin);
  }

  onLoad() {
    try {
      if (!this.printFrame.contentDocument) throw new Error(CONTENT_DOCUMENT_NOT_SET);
      this.triggerLogAnalyticsCallback(printAnalyticsEvents.PRINT_IFRAME_LOADED);
      this.printFrame.focus();
      this.printFrame.contentWindow.print();
      this.triggerPostMessageForAfterPrintCallback(true);
    } catch (err) {
      if (!isEdgeHtml()) {
        // Bug fix: EdgeHTML (version <= 44) is throwing exception at this.printFrame.contentWindow.print(); due to which postMessage happens twice.
        this.triggerPostMessageForFallbackPrint(printTriggerType.PRINT_USING_HTML);
        if (err && err.message && err.message === CONTENT_DOCUMENT_NOT_SET) {
          this.triggerLogAnalyticsCallback(printAnalyticsEvents.PRINT_CONTENT_DOCUMENT_NOT_LOADED);
        } else this.triggerLogAnalyticsCallback(printAnalyticsEvents.PRINT_IFRAME_NOT_LOADED);
        this.triggerPostMessageForAfterPrintCallback(false);
      }
    } finally {
      URL.revokeObjectURL(this.pdfBlobUrl); // discarding the blob url once iframe is loaded
    }
  }

  createPrintFrame() {
    if (this.printFrame) {
      document.body.removeChild(this.printFrame);
    }

    this.printFrame = document.createElement("iframe");
    this.printFrame.style.display = "none";
    document.body.appendChild(this.printFrame);
  }

  print(pdfBuffer) {
    this.createPrintFrame();

    const blob = new Blob([pdfBuffer], {
      type: "application/pdf"
    });
    this.pdfBlobUrl = URL.createObjectURL(blob);
    this.printFrame.setAttribute("src", this.pdfBlobUrl);
    this.printFrame.onload = this.onLoad;
  }
}
